import Vue from 'vue';
import Meta from 'vue-meta';
import Router from 'vue-router';
import { sync } from 'vuex-router-sync';
import store from '@/store';
import routes from './routes';
import { ability } from '@/store';
import User from "@/services/User/User";
import renewToken from '@/api/renewToken';

Vue.use(Meta);
Vue.use(Router);

export let pendingClientSwitch = null;

export function setPendingClientSwitch(value) {
  pendingClientSwitch = value;
}

export function clearPendingClientSwitch() {
  pendingClientSwitch = null;
}

/**
 * Handle client_id query parameter for client switching.
 * Returns navigation instruction or null to continue normal flow.
 *
 * @param {Route} to - Target route
 * @param {Route} from - Source route
 * @returns {{ action: 'proceed' | 'block', next: any } | null}
 */
function handleClientSwitchParam(to, from) {
  const clientIdParam = to.query.client_id;
  if (!clientIdParam) return null;

  const targetClientId = Number(clientIdParam);
  const currentClientId = store.getters.userClientId;
  const clients = store.getters.clients;
  const targetClient = clients.find(c => c.clientId === targetClientId);

  // Build target route without client_id param
  const queryWithoutClientId = { ...to.query };
  delete queryWithoutClientId.client_id;

  // User doesn't have access to requested client
  if (!targetClient) {
    Vue.nextTick(() => {
      Vue.notify({
        title: 'Access Denied',
        text: 'You do not have access to the requested client.',
        type: 'error',
      });
    });
    return {
      action: 'proceed',
      next: { name: 'dashboard', replace: true },
    };
  }

  // Same client - just proceed without the param
  if (targetClientId === currentClientId) {
    return {
      action: 'proceed',
      next: { ...to, query: queryWithoutClientId, replace: true },
    };
  }

  // Valid different client - store destination in memory and block navigation
  setPendingClientSwitch({
    targetClientId,
    targetRoute: { path: to.path, query: queryWithoutClientId },
  });

  // Stay on current page if already somewhere (except login/home pages), else go to dashboard
  if (from.name && from.name !== 'login' && from.name !== 'home') {
    return { action: 'block', next: false };
  }

  return { action: 'block', next: { name: 'dashboard', replace: true } };
}

const router = make(routes({ authGuard, guestGuard }));

if (store) {
  sync(store, router);
}

export default router;

/**
 * Create a new router instance.
 *
 * @param  {Array} routes
 * @return {Router}
 */
function make(routes) {
  const router = new Router({
    routes,
    scrollBehavior,
    mode: 'history',
    base: process.env.BASE_URL,
  });

  // Register before guard.
  router.beforeEach(async (to, from, next) => {
    const userLoggedIn = store.getters.authCheck;
    Vue.$model = to.meta.model;

    const { meta } = to;
    if (meta.progress !== undefined) {
      const { progress } = meta;
      // parse meta tags
      router.app.$Progress.parseMeta(progress);
    }
    //  start the progress bar
    router.app.$Progress.start();

    // If the access token has expired, try to renew it silently from the
    // still-valid Auth0 SSO session before tearing anything down, and fall back
    // to a login redirect (never a logout) if that fails. Without this, an
    // expired token on navigation destroyed the SSO session and bounced the
    // user back to Auth0 even though their session was still valid.
    let renewalFailed = false;
    if (userLoggedIn && User.hasExpiredToken(store.getters.authExpiry)) {
      try {
        await renewToken();
      } catch (e) {
        renewalFailed = true;
        // Renewal failed. For an MFA-enforced user the silent-auth iframe can
        // never succeed (an iframe cannot complete an MFA challenge), so
        // redirecting to login bounces off the still-alive SSO session and
        // loops. Log out instead: this destroys the SSO session so the next
        // load lands on the interactive login (which CAN do MFA) and the loop
        // terminates. next(false) below aborts this navigation — logout
        // navigates the browser and supersedes it. .catch contains a logout
        // failure so it cannot escape as an unhandled rejection. (CS-1929)
        store.dispatch('logout', { expired: true }).catch(() => {});
      }
    }

    if (renewalFailed) {
      // The login redirect navigates the browser, which supersedes this
      // navigation. Abort it explicitly rather than just returning: if the
      // redirect itself threw, a bare return would leave the navigation
      // unresolved and the progress bar spinning forever.
      next(false);
      return;
    }

    {
      const isOnLoginPage = to.name === 'login';

      if (!store.getters.authCheck && store.getters.authToken && !isOnLoginPage) {
        try {
          await store.dispatch('fetchUser');
        } catch (e) {
          console.log(e);
        }
      }

      // If user is logged in but has no clientId set, and is not on the login screen
      if (userLoggedIn && !store.getters.userClientId && !isOnLoginPage) {
        try {
          await store.dispatch('logout', { expired: true });
        } catch (e) {
          console.log(e);
        }
      }

      // Check for client_id query param and handle client switching
      if (userLoggedIn) {
        const clientSwitchResult = handleClientSwitchParam(to, from);
        if (clientSwitchResult) {
          next(clientSwitchResult.next);
          return;
        }
      }

      // Control route permissions.
      //
      // Previously this used forEach without returning, so a route failing N
      // permissions called next() N+1 times (once per failure, plus the
      // trailing next() below). vue-router honours only the first call and
      // logs an error for the rest, so the navigation outcome was already
      // correct — but it emitted a toast per failed permission and a console
      // error. Short-circuit instead: one toast, exactly one next().
      if (meta.permissions) {
        const isDenied = meta.permissions
          .some(p => ability.cannot(p.action, p.subject));

        if (isDenied) {
          Vue.notify({
            title: 'Access denied',
            text: 'You do not have access to this area',
            type: 'error',
          });

          next({ name: 'dashboard' });
          return;
        }
      }

      next();
    }
  });

  // Register after hook.
  router.afterEach((to, from) => {
    // For convenience, we add the class name of the route to the body
    document.body.className = to.name || document.body.className;
    if (to.meta && to.meta.title) {
      document.title = to.meta.title;
    }
    router.app.$nextTick(() => {
      // router.app.$loading.finish();
      router.app.$Progress.finish();
    });
  });

  return router;
}

/**
 * Redirect to login if guest.
 *
 * @param  {Array} routes
 * @return {Array}
 */
function authGuard(routes) {
  return beforeEnter(routes, async (to, from, next) => {

    const { redirect } = store.getters;

    if (!store.getters.authCheck) {
      sessionStorage.setItem('authTargetUrl', to.fullPath);
      next({ name: 'home' });
    } else if (redirect) {
      // Reset the redirect as we are done here
      await store.dispatch('setRedirect', null);

      next(redirect);
    } else {
      // Check terms and conditions
      next();
    }
  });
}

/**
 * Redirect home if authenticated.
 *
 * @param  {Array} routes
 * @return {Array}
 */
function guestGuard(routes) {
  return beforeEnter(routes, async (to, from, next) => {
    if (store.getters.authCheck) {
      next({ name: 'dashboard' });
    } else {
      // If a user was trying to reach a specific page before logging in, we record
      // it here so we can redirect the user later
      const redirect = window.location.pathname;

      // We currently want to restrict this logic to only the /eula page
      if (redirect === '/eula') {
        await store.dispatch('setRedirect', redirect);
      }
      next();
    }
  });
}

/**
 * Apply beforeEnter guard to the routes.
 *
 * @param  {Array} routes
 * @param  {Function} beforeEnter
 * @return {Array}
 */
function beforeEnter(routes, beforeEnter) {
  return routes.map(route => ({ ...route, beforeEnter }));
}

/**
 * @param  {Route} to
 * @param  {Route} from
 * @param  {Object|undefined} savedPosition
 * @return {Object}
 */
function scrollBehavior(to, from, savedPosition) {
  if (savedPosition) {
    return savedPosition;
  }

  const position = {};

  if (to.hash) {
    position.selector = to.hash;
  }

  if (to.matched.some(m => m.meta.scrollToTop)) {
    position.x = 0;
    position.y = 0;
  }

  return position;
}
